import * as React from 'react';
import { ItemDetail } from '@shopee/flow-types';

export type RequestAddItemToCart = (
  shopId: number,
  itemId: number,
  onAddToCartSuccess?: () => void,
  extraAddToCartTrackingData?: any,
  options?: { showToastAfterSuccess?: boolean },
  spuInfo?: {
    vItemId: number;
    vShopId: number;
    vModelId: number;
    rModelId: number;
  }
) => Promise<void>;

export const AddToCartContext = React.createContext<RequestAddItemToCart>(
  (_shopId, _itemId) => Promise.resolve()
);

/**
 * Short hand for exposing the requestAddItemToCart
 * from AddToCartContext Context as a prop
 */
export type WithAddToCartAsPropProps = {
  requestAddItemToCart: RequestAddItemToCart;
};

export function withAddToCartAsProp<Config>(
  Component: React.ComponentType<Config & WithAddToCartAsPropProps>
): React.ComponentType<Config> {
  function WithAddToCartAsProp(props: Config) {
    const requestAddItemToCart = React.useContext(AddToCartContext);
    return <Component {...props} requestAddItemToCart={requestAddItemToCart} />;
  }
  return WithAddToCartAsProp;
}

/**
 * Redirect to PDP if one of these conditions is fulfilled:
 *
 * - Item is sold out
 * - Item status is not normal
 * - Is adult item and hasn't given consent
 * - Current user owns this item
 */
export function shouldRedirectToProductPage(
  item: ItemDetail | null | undefined,
  {
    isUserAdult,
    loggedInUserShopId,
  }: {
    isUserAdult: boolean;
    loggedInUserShopId: number | null | undefined;
  }
): boolean {
  if (!item) {
    return true;
  }

  if (item.stock === 0) {
    return true;
  }
  if (item.status !== 1) {
    // 1 means truly normal; some others may also mean normal (e.g. 8 == ITEM_NORMAL_UNLIST), but we do not care
    // Ref: https://confluence.shopee.io/display/~jm.ma@shopee.com/Product+Detail+Page#ProductDetailPage-Coreserverstatus
    return true;
  }
  if (!!item.is_adult && !isUserAdult) {
    return true;
  }

  if (loggedInUserShopId && loggedInUserShopId === item.shopid) {
    return true;
  }

  return false;
}
